import type {
  ArenaClientCommand,
  ArenaCommandDisposition,
  ArenaRealtimeConnection,
  ArenaRealtimePort,
  ArenaSnapshotEvent,
  ArenaServerEvent,
  ArenaWireCommand,
} from "../domain/arenaProtocol";

const RECONNECT_DELAYS_MS = [400, 800, 1_600, 3_000, 5_000] as const;

function isArenaServerEvent(value: unknown): value is ArenaServerEvent {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<ArenaServerEvent>;
  return (
    (candidate.type === "arena.snapshot" || candidate.type === "arena.problem") &&
    typeof candidate.eventSequence === "number" &&
    Number.isSafeInteger(candidate.eventSequence) &&
    typeof candidate.serverNow === "string"
  );
}

function commandStillApplies(command: ArenaClientCommand, snapshot: ArenaSnapshotEvent): boolean {
  const match = snapshot.match;
  if (command.type === "arena.queue") {
    return match.status === "idle";
  }
  if (command.type === "arena.move") {
    return (
      match.status === "choosing" &&
      match.matchId === command.matchId &&
      match.currentTurn?.index === command.turnIndex &&
      match.currentTurn.playerZone === null
    );
  }
  if (command.type === "arena.cancel") return match.status === "matchmaking";
  if (command.type === "arena.dismiss") return match.status === "result";
  return true;
}

export class WebSocketArenaTransport implements ArenaRealtimePort {
  readonly #endpoint: string;

  constructor(endpoint: string) {
    const parsed = new URL(endpoint, window.location.href);
    if (parsed.protocol !== "ws:" && parsed.protocol !== "wss:") {
      throw new Error("Arena realtime endpoint must use ws:// or wss://");
    }
    this.#endpoint = parsed.toString();
  }

  connect(options: {
    initData?: string;
    accessToken?: string;
    getAccessToken?(): string;
    lastEventSequence: number;
    onEvent(event: ArenaServerEvent): void;
    onCommandDisposition?(command: ArenaClientCommand, disposition: ArenaCommandDisposition): void;
    onDisconnect(): void;
  }): ArenaRealtimeConnection {
    let socket: WebSocket | null = null;
    let reconnectTimer = 0;
    let reconnectAttempt = 0;
    let closedByClient = false;
    let authenticated = false;
    let lastEventSequence = options.lastEventSequence;
    const waitingForAuthentication: ArenaClientCommand[] = [];

    const sendWire = (command: ArenaWireCommand): void => {
      if (socket?.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify(command));
    };

    const scheduleReconnect = (): void => {
      if (closedByClient || reconnectTimer !== 0) return;
      const delay = RECONNECT_DELAYS_MS[Math.min(reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)] ?? 5_000;
      reconnectAttempt += 1;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = 0;
        open();
      }, delay);
    };

    const open = (): void => {
      if (closedByClient) return;
      const opened = new WebSocket(this.#endpoint);
      socket = opened;
      opened.addEventListener("open", () => {
        if (closedByClient || socket !== opened) return;
        authenticated = false;
        const accessToken = options.getAccessToken?.() ?? options.accessToken;
        const authenticate: ArenaWireCommand = {
          type: "arena.authenticate",
          lastEventSequence,
          ...(accessToken !== undefined ? { accessToken } : {}),
          ...(options.initData !== undefined ? { initData: options.initData } : {}),
        };
        sendWire(authenticate);
      });
      opened.addEventListener("message", (message) => {
        if (closedByClient || socket !== opened) return;
        if (typeof message.data !== "string") return;
        try {
          const parsed: unknown = JSON.parse(message.data);
          if (!isArenaServerEvent(parsed)) return;
          lastEventSequence = Math.max(lastEventSequence, parsed.eventSequence);
          if (!authenticated && parsed.type === "arena.snapshot") {
            authenticated = true;
            reconnectAttempt = 0;
            options.onEvent(parsed);
            const commands = waitingForAuthentication.splice(0);
            for (const command of commands) {
              if (commandStillApplies(command, parsed)) {
                sendWire(command);
                options.onCommandDisposition?.(command, "sent");
              } else {
                options.onCommandDisposition?.(command, "superseded");
              }
            }
            return;
          }
          if (!authenticated && parsed.type === "arena.problem") {
            // The live gateway reports JWT rejection as INVALID_COMMAND and
            // then closes with 4401. No game command was sent before this
            // handshake: refresh authorization once instead of repeatedly
            // reconnecting with the rejected credential and forcing /sync.
            closedByClient = true;
            waitingForAuthentication.length = 0;
            window.clearTimeout(reconnectTimer);
            reconnectTimer = 0;
            opened.close(1_000, "refresh authorization");
            socket = null;
            options.onEvent({ ...parsed, code: "AUTH_REQUIRED" });
            return;
          }
          options.onEvent(parsed);
        } catch {
          // A malformed frame is ignored; the next authoritative snapshot repairs the client.
        }
      });
      opened.addEventListener("close", () => {
        if (socket !== opened) return;
        socket = null;
        authenticated = false;
        if (closedByClient) return;
        options.onDisconnect();
        scheduleReconnect();
      });
      opened.addEventListener("error", () => {
        if (socket === opened) opened.close();
      });
    };

    open();
    return {
      send(command): void {
        if (socket?.readyState === WebSocket.OPEN && authenticated) {
          sendWire(command);
          options.onCommandDisposition?.(command, "sent");
          return;
        }
        const duplicate = command.type !== "arena.resume" && waitingForAuthentication.some(
          (queued) => queued.type !== "arena.resume" && queued.commandId === command.commandId,
        );
        if (!duplicate) {
          waitingForAuthentication.push(command);
        }
      },
      close(): void {
        closedByClient = true;
        window.clearTimeout(reconnectTimer);
        reconnectTimer = 0;
        socket?.close(1_000, "client closed");
        socket = null;
        waitingForAuthentication.length = 0;
      },
    };
  }
}
